import {
  Building2,
  CalendarPlus,
  FileText,
  Inbox,
  type LucideIcon,
  Shield,
  Sparkles,
  Stethoscope,
  UserRound,
  Users,
} from 'lucide-react';

import { P } from '@/features/permissions';
import type { Edition } from '@/lib/auth/AuthContext';

export interface NavGroup {
  items: NavItem[];
  label: string;
  /** When set, hide this entire group unless the current edition matches. */
  requiredEdition?: Edition;
}

export interface NavItem {
  badge?: number;
  external?: boolean;
  href: string;
  icon: LucideIcon;
  isActive?: (pathname: string) => boolean;
  /** When set, hide this item unless the current edition matches. */
  requiredEdition?: Edition;
  requiredPermission?: string;
  requiredPermissions?: string[];
  title: string;
}

// NOTE: Items here can be hidden in the personal-edition UI by setting
// `requiredEdition: 'professional'`. Plan 5 marks every item that should not
// appear in personal mode. Add `requiredEdition` to new items as appropriate.
export const mainNavigation: NavGroup[] = [
  {
    label: 'Clinical',
    items: [
      {
        icon: Stethoscope,
        href: '/encounters',
        title: 'Encounters',
        requiredEdition: 'professional',
        requiredPermission: P.READ_ENCOUNTER,
        isActive: (pathname) => pathname.startsWith('/encounters'),
      },
      {
        icon: UserRound,
        href: '/patients',
        title: 'Patients',
        requiredEdition: 'professional',
        requiredPermission: P.READ_PATIENT,
        isActive: (pathname) => pathname.startsWith('/patients'),
      },
      {
        icon: Inbox,
        title: 'Inbox',
        href: '/documents',
        requiredEdition: 'professional',
        requiredPermission: P.READ_DOCUMENT,
        isActive: (pathname) => pathname.startsWith('/documents'),
      },
      {
        icon: Sparkles,
        href: '/quick-start',
        title: 'Quick Start',
        requiredEdition: 'professional',
        isActive: (pathname) => pathname.startsWith('/quick-start'),
      },
    ],
  },
  {
    label: 'AI Tools',
    requiredEdition: 'professional',
    items: [
      {
        icon: CalendarPlus,
        href: '/upload-schedule',
        title: 'Upload Schedule',
        requiredEdition: 'professional',
        requiredPermission: P.CREATE_ENCOUNTER,
        isActive: (pathname) => pathname.startsWith('/upload-schedule'),
      },
    ],
  },
  {
    label: 'Administration',
    requiredEdition: 'professional',
    items: [
      {
        icon: FileText,
        href: '/templates',
        title: 'Templates',
        requiredEdition: 'professional',
        requiredPermission: P.READ_TEMPLATE,
        isActive: (pathname) => pathname.startsWith('/templates'),
      },
      {
        icon: Building2,
        href: '/organization',
        title: 'Organization',
        requiredEdition: 'professional',
        requiredPermission: P.MANAGE_ORGANIZATION,
        isActive: (pathname) => pathname.startsWith('/organization'),
      },
      {
        icon: Users,
        href: '/users',
        title: 'Users',
        requiredEdition: 'professional',
        requiredPermission: P.MANAGE_USERS,
        isActive: (pathname) => pathname.startsWith('/users'),
      },
      {
        icon: Shield,
        href: '/roles',
        title: 'Roles',
        requiredEdition: 'professional',
        requiredPermission: P.MANAGE_USERS,
        isActive: (pathname) => pathname.startsWith('/roles'),
      },
    ],
  },
];
